import { Settings, Baby, BedDouble, UtensilsCrossed } from "lucide-react";
import { AutosaveStatus } from "@/components/common/AutosaveStatus";
import type { AutosaveStatus as AutosaveStatusValue } from "@/hooks/useAutosave";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion } from "@/components/customer/events/Accordion";
import { useTranslation } from "@/hooks/useTranslation";
import type { EventRsvpSettings } from "@/services/api/eventSettings";
import { validateRsvpDeadline } from "./rsvpDeadline";

interface RsvpSettingsAccordionProps {
  settings: EventRsvpSettings;
  onChange: (settings: EventRsvpSettings) => void;
  /** JUB-152: changes save as they are made; these drive the status chip. */
  status: AutosaveStatusValue;
  restored?: boolean;
  onRetry: () => void;
  onDiscard?: () => void;
  onSaveNow: () => void;
  eventDate?: string | null;
  /** JUB-209: GuestsTab hides the redundant date-only input in favour of the unified RSVP cutoff datetime card. */
  showDeadlineField?: boolean;
}

export function RsvpSettingsAccordion({
  settings,
  onChange,
  status,
  restored,
  onRetry,
  onDiscard,
  onSaveNow,
  eventDate,
  showDeadlineField = true,
}: RsvpSettingsAccordionProps) {
  const { t } = useTranslation();

  const deadlineError = validateRsvpDeadline(settings.dueDate, eventDate, t);
  const maxDate = eventDate ? eventDate : undefined;

  return (
    <Accordion
      title={t("events.form.rsvpSettings")}
      icon={<Settings className="h-6 w-6 text-pink-600 dark:text-pink-400" />}
      defaultOpen={false}
    >
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <RsvpToggle
            title={t("events.form.enableRsvp")}
            description={t("events.form.enableRsvpHint")}
            checked={settings.rsvpEnabled}
            onChange={(value) => onChange({ ...settings, rsvpEnabled: value })}
          />
          <RsvpToggle
            title={t("events.form.emailNotifications")}
            description={t("events.form.emailNotificationsHint")}
            checked={settings.emailNotifications}
            onChange={(value) => onChange({ ...settings, emailNotifications: value })}
          />
          <RsvpToggle
            icon={<Baby className="h-4 w-4 text-pink-500" />}
            title={t("events.form.kidsEnabled")}
            description={t("events.form.kidsEnabledHint")}
            checked={settings.kidsEnabled}
            onChange={(value) => onChange({ ...settings, kidsEnabled: value })}
          />
          <RsvpToggle
            icon={<BedDouble className="h-4 w-4 text-pink-500" />}
            title={t("events.form.accommodationEnabled")}
            description={t("events.form.accommodationEnabledHint")}
            checked={settings.accommodationEnabled}
            onChange={(value) => onChange({ ...settings, accommodationEnabled: value })}
          />
        </div>

        {showDeadlineField && (
          <div>
            <Label>{t("events.form.rsvpDeadline")}</Label>
            <Input
              type="date"
              className={`mt-2 ${deadlineError ? "border-destructive" : ""}`}
              value={settings.dueDate ?? ""}
              onChange={(event) => {
                const newDate = event.target.value || null;
                onChange({ ...settings, dueDate: newDate });
              }}
              min={new Date().toISOString().split('T')[0]}
              max={maxDate}
              disabled={!eventDate}
            />
            {!eventDate && (
              <p className="text-xs text-destructive mt-1">
                {t("validation.eventDateRequiredForDeadline")}
              </p>
            )}
            {deadlineError && (
              <p className="text-xs text-destructive mt-1">{deadlineError}</p>
            )}
            {!deadlineError && eventDate && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("events.form.rsvpDeadlineHint")}
              </p>
            )}
          </div>
        )}

        <div className="rounded-xl border bg-muted/30 p-4">
          <div className="flex items-center gap-2 mb-4">
            <UtensilsCrossed className="h-5 w-5 text-pink-500" />
            <p className="font-semibold">
              {t("events.form.mealPreferences") || t("rsvp.menuPreference")}
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {(["standard", "vegan", "vegetarian"] as const).map((option) => (
              <div
                key={option}
                className="flex items-center justify-between rounded-lg border bg-background px-3 py-2"
              >
                {/* One line per option: the translated name is the title (JUB-193). */}
                <p className="font-medium">{t(`events.form.meal.${option}`)}</p>
                <Switch
                  checked={settings.mealOptions?.[option] ?? false}
                  onCheckedChange={(value) =>
                    onChange({
                      ...settings,
                      mealOptions: { 
                        standard: settings.mealOptions?.standard ?? false,
                        vegan: settings.mealOptions?.vegan ?? false,
                        vegetarian: settings.mealOptions?.vegetarian ?? false,
                        [option]: value 
                      },
                    })
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <AutosaveStatus
          status={status}
          restored={restored}
          onRetry={onRetry}
          onDiscard={onDiscard}
          onSaveNow={onSaveNow}
        />
      </div>
    </Accordion>
  );
}

function RsvpToggle({
  title,
  description,
  checked,
  onChange,
  icon,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border bg-background px-4 py-3">
      <div className="mr-4">
        <div className="flex items-center gap-2">
          {icon}
          <p className="font-semibold text-foreground">{title}</p>
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

